/**
 * Extracts and normalizes search terms from a search string
 * @param search - The search string to process
 * @returns Array of lowercase search terms with extra whitespace removed
 * @example
 * extractSearchTerms("  React  TypeScript  ") // returns ["react", "typescript"]
 * extractSearchTerms(null) // returns []
 */
export const extractSearchTerms = (search: string | null): string[] => {
  if (!search) return []
  return search.replace(/\s+/g, ' ').trim().toLowerCase().split(' ')
}

/**
 * Checks if an item matches all search terms within its searchable text
 * @param item - The item to search within
 * @param searchQuery - The search string to match against
 * @param getSearchableText - Function that returns the searchable text from the item
 * @returns True if all search terms are found in the item's searchable text
 * @example
 * const item = { title: "React TypeScript Tutorial" };
 * matchesSearch(item, "react type", item => item.title); // returns true
 */
export const matchesSearch = <T>(
  item: T,
  searchQuery: string | null,
  getSearchableText: (item: T) => string
): boolean => {
  const searchTerms = extractSearchTerms(searchQuery)
  if (searchTerms.length === 0) return true

  const text = getSearchableText(item).toLowerCase()
  return searchTerms.every(term => text.includes(term))
}

/**
 * Creates a custom filterOptions function for Select components
 * that filters options based on searchable text from source data
 * @param sourceData - Array of source data items to search within
 * @param getSearchableText - Function that returns the searchable text from a source item
 * @param getValue - Function that returns the value/id from a source item to match with option.value
 * @returns A filterOptions function compatible with baseui Select component
 * @example
 * const segments = [{ id: 1, name: "VIP Customers" }];
 * const filterFn = createFilterOptions(segments, s => s.name, s => s.id);
 * // Use with Select: <Select filterOptions={filterFn} ... />
 */
export const createFilterOptions = <T>(
  sourceData: T[],
  getSearchableText: (item: T) => string,
  getValue: (item: T) => any
) => {
  return (options: readonly any[], filterValue: string) => {
    if (!filterValue) return options

    return options.filter(option => {
      const sourceItem = sourceData.find(
        item => getValue(item) === option.value
      )
      if (!sourceItem) return false

      return matchesSearch(sourceItem, filterValue, getSearchableText)
    })
  }
}
